
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, lastValueFrom, Observable, of, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApplicationFunctionality } from '../model/application-functionality';
import { ApplicationModule } from '../model/application-module';
import { EnumLegalEntityRole, LegalEntityInfo } from '../model/legal-entity-info';
import { Permission } from '../model/permission';
import { Role } from '../model/role';
import { EnumUserLegalEntityRole, EnumUserRole } from '../model/user-basic-info';
import { UserInfo } from '../model/user-info';
import { ErrorHandlerAction, HttpErrorHandler } from '../utilities/http-error-handler';
import { SharedUtils } from '../utilities/shared-utils';
import { WinoCookieService } from './wino-cookie.service';
import { UserLegalEntityAttributesBasicInfo } from '../model/user-legal-entity-attributes-basic-info';
import { EnumEntity } from '../model/entity';

@Injectable({
  providedIn: 'root'
})

export class PermissionService {

    private static readonly permissionBaseUrl: string = environment.membershipBackendAddress + '/api/permission';
    private static readonly functionalityBaseUrl: string = environment.membershipBackendAddress + '/api/applicationFunctionality';
    private static readonly componentBaseUrl: string = environment.membershipBackendAddress + '/api/applicationComponent';
    private static readonly roleBaseUrl: string = environment.membershipBackendAddress + '/api/role';
    private static readonly userBaseUrl: string = environment.membershipBackendAddress + '/api/user';
  
    private static constantsAllreadyPopulated: boolean = false;
  
    private static permissions: Array<Permission> = new Array<Permission>();
  
    private static readonly ROLES_NAME_BY_FUNCTIONALITY_NAME: Map<string, Set<string>> = new Map<string, Set<string>>();

    public titleBarData: Subject<{title: string, offcanvas: boolean, imageName?: string}> = new Subject<{title: string, offcanvas: boolean, imageName?: string}>();
  
    constructor(private readonly httpClient: HttpClient, private readonly httpErrorHandler: HttpErrorHandler,
                  private readonly cookieService: WinoCookieService, private readonly router: Router) { }

    public retrievePermissions(): Promise<boolean> {
        const moduleNameArray: string[] = [environment.membershipModuleName, environment.libraryModuleName, environment.publishingModuleName];
        console.debug(Object.values(moduleNameArray));

        return new Promise<boolean>( resolve => {
            lastValueFrom(this.httpClient.get<RetrievePermissionsResponse>(`${PermissionService.permissionBaseUrl}/applicationComponentNameIn`, {
                params: { 'applicationComponentsName': Object.values(moduleNameArray) }
            })).then(data => {
                PermissionService.permissions = data._embedded.permissionRepresentationList;
                PermissionService.populateClientStaticConstants();
                PermissionService.ROLES_NAME_BY_FUNCTIONALITY_NAME.forEach( (value: Set<string>, key: string) => {
                    value.forEach(role => {
                        // console.log(key + " - " + role);
                    });
                });
                resolve(true);
            }).catch( (err: Error) => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(false);
            });
        });
    }

    public getApplicationComponents(): Promise<Set<ApplicationModule>> {
        return new Promise<Set<ApplicationModule>>( resolve => {
            lastValueFrom(this.httpClient.get<GetApplicationComponentsResponse>(`${PermissionService.componentBaseUrl}/all`)).then(data => {
                let applicationComponents: Set<ApplicationModule> = new Set<ApplicationModule>();
                applicationComponents = data._embedded.applicationComponentRepresentationList;
                resolve(applicationComponents);
            }).catch( (err: Error) => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(undefined);
            });
        });
    }

    public passwordLost(emailAddress: string): Promise<boolean> {
        return new Promise<boolean>( (resolve, reject) => {
            lastValueFrom(this.httpClient.post<boolean>(`${PermissionService.userBaseUrl}/passwordLost`, emailAddress)).then( (data: boolean) => {
                resolve(data || false);
            });
        });
    }

    public getPermissionsByComponentName(applicationComponentName: string): Promise<Set<Permission>> {
        return new Promise<Set<Permission>>( resolve => {
            lastValueFrom(this.httpClient.get<GetPermissionsResponse>(`${PermissionService.permissionBaseUrl}/byApplicationComponent`, {
                params: { 'applicationComponentName': applicationComponentName }
            })).then(data => {
                if (data._embedded) {
                    let permissionList: Set<Permission> = new Set<Permission>();
                    permissionList = data._embedded.permissionRepresentationList;
                    resolve(permissionList);
                } else {
                    resolve(undefined);
                }
            }).catch( (err) => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(undefined);
            });
        });
    }

    public getPermissionsByComponentId(applicationComponentId: number): Promise<Set<Permission>> {
        return new Promise<Set<Permission>>( resolve => {
            lastValueFrom(this.httpClient.get<GetPermissionsResponse>(`${PermissionService.permissionBaseUrl}/byApplicationComponent`, {
                params: { 'applicationComponentId': applicationComponentId }
            })).then(data => {
                if (data._embedded) {
                    let permissionList: Set<Permission> = new Set<Permission>();
                    permissionList = data._embedded.permissionRepresentationList;
                    resolve(permissionList);
                } else {
                    resolve(undefined);
                }
            }).catch( (err: Error) => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(undefined);
            });
        });
    }

    public getPermissionsByFunctionalityId(applicationFunctionalityId: number): Promise<Set<Permission>> {
        return new Promise<Set<Permission>>( resolve => {
            lastValueFrom(this.httpClient.get<GetPermissionsResponse>(`${PermissionService.permissionBaseUrl}/byApplicationFunctionality`, {
                params: { 'applicationFunctionalityId': applicationFunctionalityId }
            })).then(data => {
                if (data._embedded) {
                    let permissionList: Set<Permission> = new Set<Permission>();
                    permissionList = data._embedded.permissionRepresentationList;
                    resolve(permissionList);
                } else {
                    resolve(undefined);
                }
            }).catch( (err: Error) => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(undefined);
            });
        });        
    }

    public getFunctionalitiesByComponentName(applicationComponentName: string): Promise<Set<ApplicationFunctionality>> {
        return new Promise<Set<ApplicationFunctionality>>( resolve => {
            lastValueFrom(this.httpClient.get<GetApplicationFunctionalitiesResponse>(`${PermissionService.functionalityBaseUrl}/byApplicationComponent`, {
                params: { 'applicationComponentName': applicationComponentName }
            })).then(data => {
                let functionalityList: Set<ApplicationFunctionality> = new Set<ApplicationFunctionality>();
                functionalityList = data._embedded.applicationFunctionalityRepresentationList;
                resolve(functionalityList);
            }).catch( (err: Error) => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(undefined);
            });
        });
    }

    public getFunctionalitiesByComponentId(applicationComponentId: number): Promise<Set<ApplicationFunctionality>> {
        return new Promise<Set<ApplicationFunctionality>>( resolve => {
            lastValueFrom(this.httpClient.get<GetApplicationFunctionalitiesResponse>(`${PermissionService.functionalityBaseUrl}/byApplicationComponent`, {
                params: { 'applicationComponentId': applicationComponentId }
            })).then(data => {
                let functionalityList: Set<ApplicationFunctionality> = new Set<ApplicationFunctionality>();
                if(data._embedded) {
                    functionalityList = data._embedded.applicationFunctionalityRepresentationList;
                    resolve(functionalityList);
                } else {
                    resolve(undefined);
                }
            }).catch( (err: Error) => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(undefined);
            });
        });
    }

    public getRoles(): Promise<Set<Role>> {
        return new Promise<Set<Role>>( resolve => {
            lastValueFrom(this.httpClient.get<GetRolesResponse>(`${PermissionService.roleBaseUrl}/all`)).then(data => {
                let roleList: Set<Role> = new Set<Role>();
                roleList = data._embedded.roleRepresentationList;
                resolve(roleList);
            }).catch( (err: Error) => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(undefined);
            });
        });
    }

    public addFunctionality(functionality: ApplicationFunctionality): Observable<ApplicationFunctionality> {
        return this.httpClient.post<ApplicationFunctionality>(`${PermissionService.functionalityBaseUrl}/new`, functionality).pipe(catchError((err: Error) => {
            const message: string = this.httpErrorHandler.handleError(err, "PermissionService - addFunctionality");
            console.log(message);
            return of(null);
        }));
    }

    public deleteFunctionality(functionality: ApplicationFunctionality): Observable<ApplicationFunctionality> {
        return this.httpClient.post<ApplicationFunctionality>(`${PermissionService.permissionBaseUrl}/deleteApplicationFunctionality`, functionality).pipe(catchError(err => {
            this.httpErrorHandler.handleError(err, "PermissionService - deleteFunctionality");
            return of(null);
        }));
    }

    public updateFunctionalityPermissions(functionalityId: number, permissions: Set<Permission>): Promise<Set<Permission>> {
        const permissionsArray: Permission[] = Array.from(permissions);
        return new Promise<Set<Permission>>( (resolve, reject) => {
            lastValueFrom(this.httpClient.post<GetPermissionsResponse>(`${PermissionService.permissionBaseUrl}/functionalityPermissions/${functionalityId.toString()}`, Object.values(permissionsArray)).pipe(catchError( err => {
                const message: string = this.httpErrorHandler.handleError(err, "PermissionService - updateFunctionalityPermissions");
                return of(null);
            }))).then(data => {
                if (data._embedded) {
                    let permissionList: Set<Permission> = new Set<Permission>();
                    permissionList = data._embedded.permissionRepresentationList;
                    resolve(permissionList);
                } else {
                    resolve(undefined);
                }
            }).catch( err => {
                this.httpErrorHandler.handleError(err, "PermissionService");
                resolve(undefined);
            });
        });
    }
    
    private checkAuthentication(): boolean {
        
        //console.log("PERMISSION SERVICE - isAutheticated?");
        const isAuthenticated: string = this.cookieService.checkAuthentication();
        //console.log("PermissionService - checkAuthentication: " + isAuthenticated);
        
        if ( isAuthenticated.toLowerCase() === "false" || isAuthenticated.toLowerCase() === "true") {
            return isAuthenticated.toLowerCase() === 'true';
        } else {
            this.httpClient.get<string>(`${PermissionService.userBaseUrl}/signout`).subscribe({next: (data: string) => {
                //console.log("CLEAN AUTHENTICATION. SIGN OUT... result: " + data);
                this.router.navigate(['/winoPublishing/home']);
            }, error: (err: Error) => {
                console.log("CLEAN AUTHENTICATION. Something went wrong... error is: " + err.name + " - " + err.message);
                this.router.navigate(['/genericError']);
            }, complete: () => {
                console.log("CLEAN AUTHENTICATION. SIGNOUT COMPLETED");
            }});
            return false;
        }
    }

    public login(userInfo: UserInfo): boolean {
        return this.cookieService.login(userInfo);
    }

    public acceptCookies() {
        this.cookieService.acceptCookies();
    }

    public isAuthenticated(): boolean {
        //console.log("PERMISSION SERVICE - isAutheticated?");
        //console.log(this.cookieService.getUserinfo());
        return this.cookieService.isAuthenticated();
        
    }
  
    public getClientAuthToken(): string {
        return null;
      //return this.cookieService.getAccessToken();
    }
  
    public checkAuthorization(applicationFunctionality: string): boolean {

        if (environment.permissionPolicy.toLocaleLowerCase() == 'disabled') {
            console.debug("PermissionService" + " - ATTENTION: PERMISSION & AUTHORIZATION CONTROL DISABLED!!!");
            return false;
        }

        this.checkAuthentication();

        let matchFound: boolean = false;

        const applicationFunctionalityRoleList: Set<string> =  PermissionService.ROLES_NAME_BY_FUNCTIONALITY_NAME.get(applicationFunctionality);
        if (!applicationFunctionalityRoleList) {
            console.log("PermissionService" + "  - Application Functionality NOT FOUND - functionality: \"" + 
            applicationFunctionality + ".");
            this.httpErrorHandler.handleError(new Error(
                "PERMISSION ERROR: component without permission setup... Application functionality not found: " + applicationFunctionality),
                "PermissionService.checkAuthorization",
                ErrorHandlerAction.alert_redirect,
                [
                    "Non hai i permessi per accedere a questa funzionalità. Effettua il login con un altro profilo",
                    "/winoMembership/login"
                ]
            );
            return false;
        }
        if (applicationFunctionalityRoleList.has(new Role(EnumUserRole.NONE, EnumLegalEntityRole.NONE, EnumUserLegalEntityRole.NONE).roleFullIdentifier)) {
            console.debug("PermissionService" + " - Functionality \"" + applicationFunctionality + "\" doesn't require authorization");
            matchFound = true;
            return true;
        }
        let rolesList: Array<string> = this.cookieService.getClientUserRoles();
        if (! rolesList) {
            rolesList = new Array<string>();
            rolesList.push(new Role(EnumUserRole.NONE, EnumLegalEntityRole.NONE, EnumUserLegalEntityRole.NONE).roleFullIdentifier);
        }

        rolesList.forEach(role => {
        //console.log("CHECK AUTHORIZATION: - User role: " + role);
            if (applicationFunctionalityRoleList.has(role)) {
                matchFound = true;
            }
        });

        if (matchFound) {
            //console.log("PermissionService" + " - role match FOUND - functionality: \"" + 
            //applicationFunctionality + "\" - user role list: " + rolesList);
            return true;
        } else {
            console.log("PermissionService" + "  - role match NOT FOUND - functionality: \"" + 
            applicationFunctionality + "\" - user role list: " + rolesList);
            this.httpErrorHandler.handleError(new Error(
                "PERMISSION ERROR: component without permission setup... Application functionality not found: " + applicationFunctionality),
                "PermissionService.checkAuthorization",
                ErrorHandlerAction.alert_redirect,
                [
                    "Non hai i permessi per accedere a questa funzionalità. Effettua il login con un altro profilo",
                    "/winoMembership/login"
                ]
            );
            return false;
        }
    }
  
    public isAuthorized(applicationFunctionality: string): boolean{
        try {
            let isAuthorized:boolean = false;
            const applicationFunctionalityRoleList: Set<string> =  PermissionService.ROLES_NAME_BY_FUNCTIONALITY_NAME.get(applicationFunctionality);
      
            if (!applicationFunctionalityRoleList) {
                throw new Error("ERROR: Application functionality not found: " + applicationFunctionality);
            }
      
            let rolesList: Array<string> = this.cookieService.getClientUserRoles();
            if (! rolesList) {
                rolesList = new Array<string>();
                rolesList.push(new Role(EnumUserRole.NONE, EnumLegalEntityRole.NONE, EnumUserLegalEntityRole.NONE).roleFullIdentifier);
            }
      
            rolesList.forEach(role => {
                if (applicationFunctionalityRoleList.has(role)) {
                    //console.log("ROLE MATCH FOUND - Roles list: " + rolesList);
                    isAuthorized =  true;
                }
            });
            return isAuthorized;
        } catch (err) {
            console.log(err.message);
            return false;
        }
    }
  
    private static populateClientStaticConstants(): void {
      
      if (PermissionService.constantsAllreadyPopulated) {
        return;
      }
  
      if (PermissionService.permissions != null && PermissionService.permissions.length > 0) {
        
        //let rolesList:Set<Role> = new Set<Role>();
        let functionalitiesList: Set<ApplicationFunctionality> = new Set<ApplicationFunctionality>();
  
        for (let i:number = 0; i < PermissionService.permissions.length; i++) {
          //rolesList.add(PermissionService.permissions[i].role);
          functionalitiesList.add(PermissionService.permissions[i].applicationFunctionality);
          if (i > 100) {
            break;
          }
        }
        functionalitiesList.forEach(functionality => {
          let rolesSelected: Set<string | undefined> = new Set<string | undefined>();
          PermissionService.permissions.forEach(permission => {
            if (functionality.functionalityFullIdentifier == permission.functionalityFullIdentifier) {
              rolesSelected.add(permission.roleFullIdentifier);
            }
          });
          PermissionService.ROLES_NAME_BY_FUNCTIONALITY_NAME.set(functionality.functionalityFullIdentifier, rolesSelected);
        });
      }
      PermissionService.constantsAllreadyPopulated = true;
    }

    public getClientUserInfoId(): number {
        return this.cookieService.getClientUserId();
    }

    public getClientUserInfoEmail(): string {
        return this.cookieService.getClientUserEmail();
    }

    public getClientUserInfoStatusId(): number {
        return this.cookieService.getClientUserStatusId();
    }
  
    public getClientUserInfoNickname(): string {
        return this.cookieService.getClientUserNickname();
    }

    public getCLientUserInfoInstagramNickname(): string {
        return this.cookieService.getClientUserInstagramNickname();
    }

    public getClientCitizenInfoFullName(): string | undefined {
        return this.cookieService.getClientCitizenFullName();
    }

    public getClientActiveLegalEntityInfoReference(): string | undefined {
        return this.cookieService.getClientActiveLegalEntityReference();
    }

    public getClientUserLegalEntitiesAttributesBasicInfo(): Set<UserLegalEntityAttributesBasicInfo> | undefined {
        return this.cookieService.getUserLegalEntitiesAttributesBasicInfo();
    }

    public getClientUserInfo(): UserInfo {
        return this.cookieService.getClientUserinfo();
    }

    public getClientWineSearchCategory(): string | undefined {
        return this.cookieService.getClientWineSearchCategory();
    }

    public setClientWineSearchCategory(enumEntity: EnumEntity) {
        this.cookieService.setClientWineSearchCategory(enumEntity);
    }

    public updateClientUserInfo(newUserInfo: UserInfo): boolean {
        return this.cookieService.updateClientUserInfo(newUserInfo);
    }

    public updateTitleBarData(title: string, offcanvasNeeded: boolean, imageName?: string): void {

        if ( SharedUtils.inputValueIsPresent(imageName) ) {
            this.titleBarData.next({title: title, offcanvas: offcanvasNeeded, imageName: imageName});
        } else {
            this.titleBarData.next({title: title, offcanvas: offcanvasNeeded});
        }
    }
  }
  
interface RetrievePermissionsResponse {
    _embedded: {
        permissionRepresentationList: Permission[];
    },
    _links: {
        all: {
            href: string
        }
    }
}

interface GetPermissionsResponse {
    _embedded: {
        permissionRepresentationList: Set<Permission>;
    },
    _links: {
        all: {
            href: string
        }
    }
}

interface GetApplicationComponentsResponse {
    _embedded: {
        applicationComponentRepresentationList: Set<ApplicationModule>;
    },
    _links: {
        all: {
            href: string
        }
    }
}

interface GetApplicationFunctionalitiesResponse {
    _embedded: {
        applicationFunctionalityRepresentationList: Set<ApplicationFunctionality>;
    },
    _links: {
        all: {
            href: string
        }
    }
}

interface GetRolesResponse {
    _embedded: {
        roleRepresentationList: Set<Role>;
    },
    _links: {
        all: {
            href: string
        }
    }
}
